import { TemplateProps } from '@zeus/shared';
import useFont from '../../../hooks/useFont';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Item from './components/Item';
import { getScaleSize, isBetweenRange } from '../../../utils/utils';
import { useNormalizedPixels } from '../../../hooks/useResize';
import PlaceholderItem from './components/Item/PlaceholderItem';
import { BoxComponent } from '../../../base/box/Box';
import { TypographyComponent } from '../../../base/typography/Typography';

export const Template7 = forwardRef<HTMLDivElement, TemplateProps>((props, ref) => {
  const { template, style, development, timezone, scale = 1, tenantId } = props;
  const mainRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const hasBrandLogo = template?.brandLogo && template?.brandLogo?.isEnabled && template?.brandLogo?.image;
  const { normalizePixels } = useNormalizedPixels();

  useFont(template?.fontFamily);

  useEffect(() => {
    if (mainRef.current) {
      sizeRef.current = {
        width: mainRef.current.offsetWidth,
        height: mainRef.current.offsetHeight,
      };
      mainRef.current.style.backgroundSize = `${sizeRef.current.width}px ${sizeRef.current.height}px`;
    }
  }, [style?.width, style?.height]);

  const bgStyles = () => {
    if (template?.background?.isEnabled && template?.background?.image) {
      return { backgroundImage: `url(${template?.background?.image})` };
    }

    if (template?.background?.color) {
      return { backgroundColor: template?.background?.color };
    }

    return {
      backgroundImage: `url(https://aio-dashboard-filestore-internal.s3.us-west-2.amazonaws.com/dashboard/tenant_199/location_166/brandbook/content_library/thumbnails/1764676527048-thumb_with%20backg.png)`,
    };
  };

  const { content, ...rest } = template;

  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!development) {
      const intervalId = setInterval(() => {
        setTick((prev) => prev + 1);
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, [development]);

  // All items from all menus and categories flattened into a single array
  // with schedule checks at menu, category, and item level
  const allItems = useMemo(() => {
    if (!Array.isArray(template?.content)) return [];

    return template.content.flatMap((menu) => {
      // Check menu schedule
      const isMenuActive = development ? true : isBetweenRange(menu.schedules, timezone);
      if (!isMenuActive) return [];

      return (menu.categories || []).flatMap((category) => {
        // Check category schedule
        const isCategoryActive = development ? true : isBetweenRange(category.schedules, timezone);
        if (!isCategoryActive) return [];

        return (category.items || []).filter((item) => {
          // Check item schedule
          const isItemActive = development ? true : isBetweenRange(item.schedules, timezone);
          return isItemActive;
        });
      });
    });
  }, [template?.content, development, timezone, tick]);

  function splitIntoTwoRows(items: any[]) {
    const totalItems = items.length;

    if (totalItems <= 6) {
      const firstRow = items.slice(0, 3);
      const secondRow = items.slice(3);
      return { firstRow, secondRow };
    }

    // Calculate items for first row
    const firstRowCount = Math.ceil(totalItems / 2);

    // Split into two rows
    const firstRow = items.slice(0, firstRowCount);
    const secondRow = items.slice(firstRowCount);

    return { firstRow, secondRow };
  }

  const { firstRow, secondRow } = splitIntoTwoRows(allItems);

  const placeholderItemsRow1 = firstRow.length < 3 ? new Array(5 - firstRow.length).fill(null) : [];
  const placeholderItemsRow2 = secondRow.length < 3 ? new Array(5 - secondRow.length).fill(null) : [];

  return (
    <div
      id="template7"
      ref={mainRef}
      style={{
        padding: '3%',
        overflow: 'hidden',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        ...bgStyles(),
        ...(!style ? { height: '100vw', width: '100vh' } : style),
      }}
    >
      <div
        ref={ref}
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: getScaleSize(normalizePixels(32, 'height'), scale),
        }}
      >
        {hasBrandLogo && (
          <div style={{ height: getScaleSize(normalizePixels(70, 'height'), scale) }}>
            <img
              src={template?.brandLogo?.image as string}
              alt="Brand logo"
              height={'100%'}
              width={'100%'}
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}
        {/* First Row - each item takes equal width (100% / item count) */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: '18px',
            minHeight: 0,
            justifyContent: 'space-between',
          }}
        >
          {firstRow.map((item: any, index: number) => (
            <Item
              key={`row1-${item.id}`}
              item={item}
              template={rest}
              development={development}
              timezone={timezone}
              index={index}
              lastItemIndex={firstRow.length - 1}
              scale={scale}
              tenantId={tenantId}
              hasAnyModifier={false}
            />
          ))}
          {placeholderItemsRow1.map((_, index) => (
            <PlaceholderItem index={index + firstRow.length} template={rest} scale={scale} />
          ))}
        </div>

        {/* Second Row - each item takes equal width (100% / item count) */}

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: '18px',
            minHeight: 0,
            justifyContent: 'space-between',
          }}
        >
          {secondRow.map((item: any, index: number) => (
            <Item
              key={`row2-${item.id}`}
              item={item}
              template={rest}
              development={development}
              timezone={timezone}
              index={index}
              lastItemIndex={secondRow.length - 1}
              scale={scale}
              tenantId={tenantId}
              hasAnyModifier={false}
            />
          ))}
          {placeholderItemsRow2.map((_, index) => (
            <PlaceholderItem index={index + secondRow.length + 5} template={rest} scale={scale} />
          ))}
        </div>

        {template.taxLabel.isEnabled && (
          <BoxComponent
            sx={{
              position: 'absolute',
              left: '3%',
              bottom: '1%',
            }}
          >
            <TypographyComponent
              sx={{ color: '#840577', fontWeight: '500', fontSize: normalizePixels(12, 'height') }}
              variant="subtitle1"
            >
              {template.taxLabel.taxText}
            </TypographyComponent>
          </BoxComponent>
        )}
      </div>
    </div>
  );
});
